(function () {
  const CONTRACT_VERSION = 'spirit-aroma-profile-v1';
  const TAXONOMY_VERSION = 'spirit-profile-v1';
  const INTENSITY_RANK = {
    hint: 1,
    secondary: 2,
    dominant: 3,
  };
  const FAMILY_COLORS = [
    '#d5763f',
    '#669c57',
    '#c69a35',
    '#b9824a',
    '#b7604f',
    '#8d6b50',
    '#985d72',
    '#696e73',
    '#4f8992',
    '#89705c',
    '#477d62',
    '#9a7b36',
    '#ad7649',
    '#9b5e4d',
    '#65875f',
  ];
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const DARK_FOREGROUND = '#000000';
  const LIGHT_FOREGROUND = '#ffffff';
  const RESET_SELECTION_EVENT = 'spirit-aroma:reset-selection';
  const OVERVIEW_HUB_LAYOUTS = {
    compact: {
      radius: 212,
      padding: 34,
      kickerFontSize: 26,
      statusFontSize: 27,
      statusLineHeight: 30,
      titleFontSizes: { single: 46, multiline: 42, dense: 38 },
      titleLineHeights: { single: 48, multiline: 44, dense: 40 },
      titleLineLength: 14,
      metaLineLength: 18,
      iconSize: 52,
      iconGap: 14,
      minimumGap: 14,
      maximumGap: 28,
    },
    prominent: {
      radius: 212,
      padding: 38,
      kickerFontSize: 18,
      statusFontSize: 17,
      statusLineHeight: 20,
      titleFontSizes: { single: 32, multiline: 30, dense: 28 },
      titleLineHeights: { single: 34, multiline: 32, dense: 30 },
      titleLineLength: 14,
      metaLineLength: 18,
      iconSize: 44,
      iconGap: 13,
      minimumGap: 12,
      maximumGap: 24,
    },
    standard: {
      radius: 176,
      padding: 28,
      kickerFontSize: 11,
      statusFontSize: 12.5,
      statusLineHeight: 15,
      titleFontSizes: { single: 19, multiline: 17, dense: 15 },
      titleLineHeights: { single: 21, multiline: 19, dense: 17 },
      titleLineLength: 16,
      metaLineLength: 21,
      iconSize: 38,
      iconGap: 12,
      minimumGap: 9,
      maximumGap: 18,
    },
  };

  function contractError(code) {
    const error = new Error(code);
    error.name = 'SpiritAromaContractError';
    error.code = code;
    return error;
  }

  function assertContract(condition, code) {
    if (!condition) {
      throw contractError(code);
    }
  }

  function cleanText(value) {
    return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
  }

  function cleanKey(value) {
    const key = cleanText(value);
    return /^[a-z0-9][a-z0-9_]*$/.test(key) ? key : '';
  }

  function finiteOrder(value) {
    const order = Number(value);
    return Number.isInteger(order) && order >= 0 ? order : null;
  }

  function assertExactKeys(value, expectedKeys, code) {
    assertContract(value && typeof value === 'object' && !Array.isArray(value), code);
    const actual = Object.keys(value).sort();
    const expected = [...expectedKeys].sort();
    assertContract(
      actual.length === expected.length &&
        actual.every((key, index) => key === expected[index]),
      code,
    );
  }

  function validatedLabels(labels) {
    const required = [
      'profileHeading',
      'profileSubtitle',
      'familiesHeading',
      'aromasHeading',
      'characterHeading',
      'dominant',
      'secondary',
      'hint',
      'inactive',
      'wheelInstructions',
      'profileCenter',
      'sizeNote',
      'backToOverview',
      'ofSubfamilies',
      'noneActive',
      'tapHint',
    ];
    assertExactKeys(labels, required, 'labels_invalid');
    const normalized = {};
    required.forEach((key) => {
      normalized[key] = cleanText(labels[key]);
      assertContract(normalized[key] !== '', `label_missing_${key}`);
    });
    return normalized;
  }

  function validatedFamilies(families) {
    assertContract(Array.isArray(families), 'families_invalid');
    assertContract(families.length > 0 && families.length <= 15, 'family_count_invalid');
    const seen = new Set();
    const orders = new Set();
    let previousOrder = -1;
    const normalized = families.map((family) => {
      assertExactKeys(family, ['key', 'name', 'order', 'iconUrl'], 'family_invalid');
      const key = cleanKey(family && family.key);
      const name = cleanText(family && family.name);
      const order = finiteOrder(family && family.order);
      const iconUrl = cleanText(family && family.iconUrl);
      assertContract(key !== '' && name !== '' && order !== null, 'family_invalid');
      assertContract(!seen.has(key), 'family_duplicate');
      assertContract(!orders.has(order), 'family_order_duplicate');
      assertContract(order > previousOrder, 'family_order_invalid');
      seen.add(key);
      orders.add(order);
      previousOrder = order;
      return { key, name, order, iconUrl };
    });
    return normalized;
  }

  function validatedSubfamilies(subfamilies, familyMap) {
    assertContract(Array.isArray(subfamilies), 'subfamilies_invalid');
    assertContract(subfamilies.length > 0 && subfamilies.length <= 48, 'subfamily_count_invalid');
    const seen = new Set();
    const orders = new Set();
    let previousOrder = -1;
    const normalized = subfamilies.map((subfamily) => {
      assertExactKeys(
        subfamily,
        ['key', 'name', 'familyKey', 'order', 'iconUrl'],
        'subfamily_invalid',
      );
      const key = cleanKey(subfamily && subfamily.key);
      const name = cleanText(subfamily && subfamily.name);
      const familyKey = cleanKey(subfamily && subfamily.familyKey);
      const order = finiteOrder(subfamily && subfamily.order);
      const iconUrl = cleanText(subfamily && subfamily.iconUrl);
      assertContract(key !== '' && name !== '' && familyKey !== '' && order !== null, 'subfamily_invalid');
      assertContract(familyMap.has(familyKey), 'subfamily_family_unknown');
      assertContract(!seen.has(key), 'subfamily_duplicate');
      assertContract(!orders.has(order), 'subfamily_order_duplicate');
      assertContract(order > previousOrder, 'subfamily_order_invalid');
      seen.add(key);
      orders.add(order);
      previousOrder = order;
      return { key, name, familyKey, order, iconUrl };
    });
    return normalized;
  }

  function validatedAromas(aromas, familyMap, subfamilyMap) {
    assertContract(Array.isArray(aromas), 'aromas_invalid');
    assertContract(aromas.length > 0 && aromas.length <= 12, 'aroma_count_invalid');
    const seen = new Set();
    return aromas.map((aroma) => {
      assertExactKeys(
        aroma,
        ['key', 'name', 'intensity', 'familyKey', 'subfamilyKey'],
        'aroma_invalid',
      );
      const key = cleanKey(aroma && aroma.key);
      const name = cleanText(aroma && aroma.name);
      const intensity = cleanKey(aroma && aroma.intensity);
      const familyKey = cleanKey(aroma && aroma.familyKey);
      const subfamilyKey = cleanKey(aroma && aroma.subfamilyKey);
      assertContract(
        key !== '' &&
          name !== '' &&
          Object.hasOwn(INTENSITY_RANK, intensity) &&
          familyKey !== '' &&
          subfamilyKey !== '',
        'aroma_invalid',
      );
      assertContract(!seen.has(key), 'aroma_duplicate');
      assertContract(familyMap.has(familyKey), 'aroma_family_unknown');
      assertContract(subfamilyMap.has(subfamilyKey), 'aroma_subfamily_unknown');
      assertContract(subfamilyMap.get(subfamilyKey).familyKey === familyKey, 'aroma_family_mismatch');
      assertContract(key !== 'aperitif' || intensity === 'hint', 'aperitif_intensity_invalid');
      seen.add(key);
      return { key, name, intensity, familyKey, subfamilyKey };
    });
  }

  function validatedCharacterTags(characterTags) {
    assertContract(Array.isArray(characterTags), 'character_tags_invalid');
    assertContract(characterTags.length <= 6, 'character_tag_count_invalid');
    const seen = new Set();
    const orders = new Set();
    let previousOrder = -1;
    const normalized = characterTags.map((tag) => {
      assertExactKeys(tag, ['key', 'name', 'order'], 'character_tag_invalid');
      const key = cleanKey(tag && tag.key);
      const name = cleanText(tag && tag.name);
      const order = finiteOrder(tag && tag.order);
      assertContract(key !== '' && name !== '' && order !== null, 'character_tag_invalid');
      assertContract(!seen.has(key), 'character_tag_duplicate');
      assertContract(!orders.has(order), 'character_tag_order_duplicate');
      assertContract(order > previousOrder, 'character_tag_order_invalid');
      seen.add(key);
      orders.add(order);
      previousOrder = order;
      return { key, name, order };
    });
    return normalized;
  }

  function validateContract(raw) {
    assertExactKeys(
      raw,
      [
        'contractVersion',
        'taxonomyVersion',
        'renderMode',
        'chipsOnly',
        'layout',
        'labels',
        'families',
        'subfamilies',
        'aromas',
        'characterTags',
      ],
      'contract_invalid',
    );
    assertContract(raw.contractVersion === CONTRACT_VERSION, 'contract_version_mismatch');
    assertContract(raw.taxonomyVersion === TAXONOMY_VERSION, 'taxonomy_version_mismatch');
    assertContract(raw.renderMode === 'ready_wheel' || raw.renderMode === 'ready_chips', 'render_mode_invalid');
    assertContract(typeof raw.chipsOnly === 'boolean', 'chips_only_invalid');

    const layout = raw.layout && typeof raw.layout === 'object' ? raw.layout : {};
    assertExactKeys(layout, ['key', 'name', 'categoryKey', 'styleKey'], 'layout_invalid');
    const layoutKey = cleanKey(layout.key);
    const categoryKey = cleanKey(layout.categoryKey);
    const styleKey = layout.styleKey === null || layout.styleKey === '' ? null : cleanKey(layout.styleKey);
    const layoutName = cleanText(layout.name);
    assertContract(layoutKey !== '' && categoryKey !== '' && layoutName !== '', 'layout_invalid');
    assertContract(layout.styleKey === null || layout.styleKey === '' || styleKey !== '', 'layout_style_invalid');

    const labels = validatedLabels(raw.labels);
    const families = validatedFamilies(raw.families);
    const familyMap = new Map(families.map((family) => [family.key, family]));
    const subfamilies = validatedSubfamilies(raw.subfamilies, familyMap);
    const subfamilyMap = new Map(subfamilies.map((subfamily) => [subfamily.key, subfamily]));
    families.forEach((family) => {
      assertContract(
        subfamilies.some((subfamily) => subfamily.familyKey === family.key),
        'family_without_subfamily',
      );
    });
    const aromas = validatedAromas(raw.aromas, familyMap, subfamilyMap);
    const characterTags = validatedCharacterTags(raw.characterTags);
    const hasWheelAroma = aromas.some((aroma) => aroma.intensity === 'dominant' || aroma.intensity === 'secondary');
    const expectedMode = hasWheelAroma && !raw.chipsOnly ? 'ready_wheel' : 'ready_chips';
    assertContract(raw.renderMode === expectedMode, 'render_mode_conflict');

    return {
      contractVersion: CONTRACT_VERSION,
      taxonomyVersion: TAXONOMY_VERSION,
      renderMode: expectedMode,
      chipsOnly: raw.chipsOnly,
      layout: {
        key: layoutKey,
        name: layoutName,
        categoryKey,
        styleKey,
      },
      labels,
      families,
      subfamilies,
      aromas,
      characterTags,
    };
  }

  function createSvgElement(tag, attributes) {
    const element = document.createElementNS(SVG_NS, tag);
    Object.entries(attributes || {}).forEach(([key, value]) => {
      element.setAttribute(key, String(value));
    });
    return element;
  }

  function polarPoint(center, radius, angle) {
    const radians = (angle * Math.PI) / 180;
    return {
      x: center + radius * Math.cos(radians),
      y: center + radius * Math.sin(radians),
    };
  }

  function relativeLuminance(hexColor) {
    const channels = cleanText(hexColor)
      .replace(/^#/, '')
      .match(/.{2}/g);
    if (!channels || channels.length !== 3) {
      return 0;
    }
    const linear = channels.map((channel) => {
      const value = Number.parseInt(channel, 16) / 255;
      return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
  }

  function familyPresentation(color) {
    const luminance = relativeLuminance(color);
    const whiteContrast = 1.05 / (luminance + 0.05);
    const useDarkForeground = whiteContrast < 4.5;
    return {
      foreground: useDarkForeground ? DARK_FOREGROUND : LIGHT_FOREGROUND,
      iconFilter: useDarkForeground ? 'brightness(0)' : 'brightness(0) invert(1)',
    };
  }

  function annularPath(center, innerRadius, outerRadius, startAngle, endAngle) {
    const outerStart = polarPoint(center, outerRadius, startAngle);
    const outerEnd = polarPoint(center, outerRadius, endAngle);
    const innerEnd = polarPoint(center, innerRadius, endAngle);
    const innerStart = polarPoint(center, innerRadius, startAngle);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return [
      `M ${outerStart.x.toFixed(2)} ${outerStart.y.toFixed(2)}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x.toFixed(2)} ${outerEnd.y.toFixed(2)}`,
      `L ${innerEnd.x.toFixed(2)} ${innerEnd.y.toFixed(2)}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x.toFixed(2)} ${innerStart.y.toFixed(2)}`,
      'Z',
    ].join(' ');
  }

  function strongestIntensity(aromas, key, property) {
    let strongest = 'inactive';
    aromas.forEach((aroma) => {
      if (aroma[property] !== key || aroma.intensity === 'hint') {
        return;
      }
      if ((INTENSITY_RANK[aroma.intensity] || 0) > (INTENSITY_RANK[strongest] || 0)) {
        strongest = aroma.intensity;
      }
    });
    return strongest;
  }

  function createHtmlElement(tag, attributes) {
    const element = document.createElement(tag);
    Object.entries(attributes || {}).forEach(([key, value]) => {
      if (key === 'text') {
        element.textContent = String(value);
      } else {
        element.setAttribute(key, String(value));
      }
    });
    return element;
  }

  function wrappedLines(label, maximumLength) {
    const words = cleanText(label).split(' ').filter(Boolean);
    if (words.length === 0) {
      return [];
    }
    const lines = [];
    words.forEach((word) => {
      const previous = lines[lines.length - 1];
      if (!previous || `${previous} ${word}`.length > maximumLength) {
        lines.push(word);
      } else {
        lines[lines.length - 1] = `${previous} ${word}`;
      }
    });
    return lines.length <= 3 ? lines : [lines[0], lines[1], lines.slice(2).join(' ')];
  }

  function wrappedHubLines(label, maximumLength) {
    const words = cleanText(label).split(' ').filter(Boolean);
    const units = [];
    for (let index = 0; index < words.length; index += 1) {
      if (words[index] === '&' && words[index + 1]) {
        units.push(`& ${words[index + 1]}`);
        index += 1;
      } else {
        units.push(words[index]);
      }
    }
    const lines = [];
    units.forEach((unit) => {
      const previous = lines[lines.length - 1];
      if (!previous || `${previous} ${unit}`.length > maximumLength) {
        lines.push(unit);
      } else {
        lines[lines.length - 1] = `${previous} ${unit}`;
      }
    });
    return lines.length <= 3 ? lines : [lines[0], lines[1], lines.slice(2).join(' ')];
  }

  function addTextLines(parent, lines, x, y, className, lineHeight) {
    const text = createSvgElement('text', {
      class: className,
      x,
      y,
      'text-anchor': 'middle',
    });
    const offset = -((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, index) => {
      const tspan = createSvgElement('tspan', {
        x,
        dy: index === 0 ? offset : lineHeight,
      });
      tspan.textContent = `${line}${index < lines.length - 1 ? ' ' : ''}`;
      text.append(tspan);
    });
    parent.append(text);
    return text;
  }

  function addIcon(parent, iconUrl, x, y, size, className, cueKey) {
    if (!iconUrl) {
      return null;
    }
    const attributes = {
      class: className,
      href: iconUrl,
      height: size,
      width: size,
      x: x - size / 2,
      y: y - size / 2,
      'aria-hidden': 'true',
    };
    if (cueKey) {
      attributes['data-spirit-aroma-cue'] = cueKey;
    }
    const image = createSvgElement('image', attributes);
    parent.append(image);
    return image;
  }

  function interactionLabel(contract, status) {
    return status === 'dominant'
      ? contract.labels.dominant
      : status === 'secondary'
        ? contract.labels.secondary
        : contract.labels.inactive;
  }

  function wheelModel(contract) {
    const aromasBySubfamily = new Map();
    contract.aromas.forEach((aroma) => {
      if (!aromasBySubfamily.has(aroma.subfamilyKey)) {
        aromasBySubfamily.set(aroma.subfamilyKey, []);
      }
      aromasBySubfamily.get(aroma.subfamilyKey).push(aroma);
    });
    const subfamilies = contract.subfamilies.map((subfamily) => {
      const matchingAromas = aromasBySubfamily.get(subfamily.key) || [];
      return {
        ...subfamily,
        status: strongestIntensity(contract.aromas, subfamily.key, 'subfamilyKey'),
        aromas: matchingAromas.filter((aroma) => aroma.intensity !== 'hint'),
        hints: matchingAromas.filter((aroma) => aroma.intensity === 'hint'),
      };
    });
    const families = contract.families.map((family, index) => {
      const color = FAMILY_COLORS[index % FAMILY_COLORS.length];
      const presentation = familyPresentation(color);
      return {
        ...family,
        color,
        foreground: presentation.foreground,
        iconFilter: presentation.iconFilter,
        status: strongestIntensity(contract.aromas, family.key, 'familyKey'),
        subfamilies: subfamilies.filter((subfamily) => subfamily.familyKey === family.key),
      };
    });
    const activeSubfamilies = families
      .flatMap((family) =>
        family.subfamilies
          .filter((subfamily) => subfamily.status !== 'inactive')
          .map((subfamily) => ({ ...subfamily, family })),
      )
      .sort(
        (first, second) =>
          (INTENSITY_RANK[second.status] || 0) -
            (INTENSITY_RANK[first.status] || 0) ||
          first.order - second.order,
      );
    return { contract, families, subfamilies, activeSubfamilies };
  }

  function fitSectorBlock(title, detail, geometry) {
    const padding = geometry.padding === undefined ? 8 : geometry.padding;
    const innerRadius = geometry.innerRadius + padding;
    const outerRadius = geometry.outerRadius - padding;
    const middleAngle = (geometry.startAngle + geometry.endAngle) / 2;
    const halfAngle = (geometry.endAngle - geometry.startAngle) / 2 - 0.8;
    const maximumLines = geometry.maximumLines || 3;
    const glyphWidth = 0.53;
    const inside = (x, y) => {
      const deltaX = x - geometry.center;
      const deltaY = y - geometry.center;
      const radius = Math.hypot(deltaX, deltaY);
      if (radius < innerRadius || radius > outerRadius) {
        return false;
      }
      let angle = (Math.atan2(deltaY, deltaX) * 180) / Math.PI - middleAngle;
      while (angle > 180) angle -= 360;
      while (angle < -180) angle += 360;
      return Math.abs(angle) <= halfAngle;
    };
    const fits = (x, y, width, height) =>
      [-width / 2, 0, width / 2].every((offsetX) =>
        [-height / 2, 0, height / 2].every((offsetY) =>
          inside(x + offsetX, y + offsetY),
        ),
      );
    const lineCaps = [40, 24, 18, 15, 13, 11, 9, 8, 7, 6, 5];
    const radialShifts = [0, -12, 12, -24, 24, -36, 36];
    for (
      let fontSize = geometry.maximumFontSize;
      fontSize >= geometry.minimumFontSize;
      fontSize -= 0.5
    ) {
      const lineHeight = fontSize * 1.2;
      const detailSize = fontSize * 0.8;
      for (const lineCap of lineCaps) {
        const lines = wrappedLines(title, lineCap);
        if (lines.length > maximumLines) {
          continue;
        }
        const titleWidth =
          Math.max(...lines.map((line) => line.length)) * fontSize * glyphWidth;
        const titleHeight = lines.length * lineHeight;
        const detailWidth = detail ? detail.length * detailSize * glyphWidth : 0;
        const detailHeight = detail ? detailSize * 1.45 : 0;
        const width = Math.max(titleWidth, detailWidth);
        const height =
          titleHeight + detailHeight + (geometry.iconSize ? geometry.iconSize + 6 : 0);
        for (const radialShift of radialShifts) {
          const point = polarPoint(
            geometry.center,
            geometry.anchorRadius + radialShift,
            middleAngle,
          );
          if (!fits(point.x, point.y, width, height)) {
            continue;
          }
          const top = -height / 2;
          return {
            fontSize,
            lineHeight,
            detailSize,
            lines,
            detail,
            x: point.x,
            iconY: geometry.iconSize
              ? point.y + top + geometry.iconSize / 2
              : null,
            titleY:
              point.y +
              top +
              (geometry.iconSize ? geometry.iconSize + 6 : 0) +
              titleHeight / 2,
            detailY: point.y + top + height - detailSize * 0.6,
          };
        }
      }
    }
    const lines = wrappedLines(title, 8).slice(0, maximumLines);
    const point = polarPoint(geometry.center, geometry.anchorRadius, middleAngle);
    return {
      fontSize: geometry.minimumFontSize,
      lineHeight: geometry.minimumFontSize * 1.2,
      detailSize: geometry.minimumFontSize * 0.8,
      lines,
      detail: '',
      x: point.x,
      iconY: null,
      titleY: point.y,
      detailY: point.y,
    };
  }

  function wheelPiece(options) {
    const middleAngle = (options.startAngle + options.endAngle) / 2;
    const vector = polarPoint(0, options.hoverDistance, middleAngle);
    const cueKey = `${options.type}:${options.key}`;
    const path = annularPath(
      options.center,
      options.innerRadius,
      options.outerRadius,
      options.startAngle,
      options.endAngle,
    );
    const piece = createSvgElement('g', {
      class: `product-spirit-aroma__wheel-piece product-spirit-aroma__wheel-piece--${options.type} product-spirit-aroma__wheel-piece--${options.status}`,
      style: `--spirit-aroma-family-color: ${options.color}; --spirit-aroma-family-foreground: ${options.foreground}; --spirit-aroma-family-icon-filter: ${options.iconFilter}; --spirit-aroma-hover-x: ${vector.x.toFixed(2)}px; --spirit-aroma-hover-y: ${vector.y.toFixed(2)}px;`,
      role: 'button',
      tabindex: '-1',
      'aria-label': options.ariaLabel,
      'aria-pressed': 'false',
      'data-spirit-aroma-key': options.key,
      'data-spirit-aroma-type': options.type,
      'data-spirit-aroma-cue': cueKey,
    });
    const hitArea = createSvgElement('path', {
      class: 'product-spirit-aroma__wheel-hit-area',
      d: path,
      'aria-hidden': 'true',
    });
    const visual = createSvgElement('g', {
      class: 'product-spirit-aroma__wheel-piece-visual',
    });
    const segment = createSvgElement('path', {
      class: 'product-spirit-aroma__wheel-segment',
      d: path,
    });
    visual.append(segment);
    piece.append(hitArea, visual);
    return { piece, visual, cueKey };
  }

  function ringKeyboard(nodes, initialIndex = 0) {
    if (nodes.length === 0) {
      return;
    }
    nodes.forEach((node, index) => {
      node.setAttribute('tabindex', index === initialIndex ? '0' : '-1');
      node.addEventListener('keydown', (event) => {
        const step = {
          ArrowRight: 1,
          ArrowDown: 1,
          ArrowLeft: -1,
          ArrowUp: -1,
        }[event.key];
        if (!step) {
          return;
        }
        event.preventDefault();
        const next = nodes[(index + step + nodes.length) % nodes.length];
        nodes.forEach((candidate) => candidate.setAttribute('tabindex', '-1'));
        next.setAttribute('tabindex', '0');
        next.focus();
      });
    });
  }

  function cue(root, key, active) {
    root
      .querySelectorAll(`[data-spirit-aroma-cue="${key}"]`)
      .forEach((element) => element.classList.toggle('is-cued', active));
  }

  function bindCues(root) {
    root.querySelectorAll('[data-spirit-aroma-cue]').forEach((element) => {
      if (element.dataset.spiritAromaCueBound === 'true') {
        return;
      }
      element.dataset.spiritAromaCueBound = 'true';
      const key = element.dataset.spiritAromaCue;
      element.addEventListener('pointerenter', () => cue(root, key, true));
      element.addEventListener('pointerleave', () => cue(root, key, false));
      element.addEventListener('focus', () => cue(root, key, true));
      element.addEventListener('blur', () => cue(root, key, false));
    });
  }

  function clearSelection(svg) {
    svg.querySelectorAll('.is-active').forEach((piece) => {
      piece.classList.remove('is-active');
      piece.setAttribute('aria-pressed', 'false');
    });
    svg.classList.remove('is-interacting');
  }

  function requestSelectionReset(svg) {
    svg.dispatchEvent(new Event(RESET_SELECTION_EVENT));
  }

  function clearPeerSelections(svg) {
    const root = svg.closest('[data-spirit-aroma-profile]');
    if (!root) {
      return;
    }
    root.querySelectorAll('.product-spirit-aroma__wheel-svg').forEach((peer) => {
      if (peer !== svg) {
        requestSelectionReset(peer);
      }
    });
  }

  function bindClickAway(root) {
    if (root.dataset.spiritAromaClickAwayBound === CONTRACT_VERSION) {
      return;
    }
    root.dataset.spiritAromaClickAwayBound = CONTRACT_VERSION;
    function unbind() {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('shopify:section:unload', handleUnload);
      delete root.dataset.spiritAromaClickAwayBound;
    }
    function handleClick(event) {
      if (!root.isConnected) {
        unbind();
        return;
      }
      const target = event.target instanceof Element ? event.target : null;
      const eventPath =
        typeof event.composedPath === 'function' ? event.composedPath() : [];
      const controlSelector = [
        '.product-spirit-aroma__wheel-piece',
        '.product-spirit-aroma__picker-item',
        '.product-spirit-aroma__back',
      ].join(', ');
      const clickedControl =
        eventPath.find(
          (node) =>
            node instanceof Element &&
            node.matches(controlSelector),
        ) ||
        (target && root.contains(target) ? target.closest(controlSelector) : null);
      const mountSelector = [
        '[data-spirit-aroma-wheel-desktop]',
        '[data-spirit-aroma-wheel-mobile]',
      ].join(', ');
      const protectedMount = clickedControl
        ? eventPath.find(
            (node) =>
              node instanceof Element &&
              root.contains(node) &&
              node.matches(mountSelector),
          ) || clickedControl.closest(mountSelector)
        : null;
      root.querySelectorAll('.product-spirit-aroma__wheel-svg').forEach((svg) => {
        if (!protectedMount || !protectedMount.contains(svg)) {
          requestSelectionReset(svg);
        }
      });
    }
    function handleUnload(event) {
      const scope = event.target;
      if (
        scope === root ||
        (scope instanceof Element && (scope.contains(root) || root.contains(scope)))
      ) {
        unbind();
      }
    }
    document.addEventListener('click', handleClick);
    document.addEventListener('shopify:section:unload', handleUnload);
  }

  function createOverviewHub(svg, model, compact, prominent) {
    const center = 450;
    const layout = compact
      ? OVERVIEW_HUB_LAYOUTS.compact
      : prominent
        ? OVERVIEW_HUB_LAYOUTS.prominent
        : OVERVIEW_HUB_LAYOUTS.standard;
    svg.append(
      createSvgElement('circle', {
        class: 'product-spirit-aroma__wheel-center',
        cx: center,
        cy: center,
        r: layout.radius,
      }),
    );
    const icons = createSvgElement('g', {
      class: 'product-spirit-aroma__wheel-center-icons',
    });
    const live = createSvgElement('g', {
      class: 'product-spirit-aroma__wheel-center-live',
      'aria-live': 'polite',
      'aria-atomic': 'true',
    });
    const kicker = createSvgElement('text', {
      class: 'product-spirit-aroma__wheel-center-kicker',
      x: center,
      y: center,
      'text-anchor': 'middle',
    });
    const title = createSvgElement('g', {
      class: 'product-spirit-aroma__wheel-center-title',
    });
    const meta = createSvgElement('g', {
      class: 'product-spirit-aroma__wheel-center-status',
    });
    live.append(kicker, title, meta);
    svg.append(icons, live);
    const set = (details) => {
      const kickerText = cleanText(details.kicker).toUpperCase();
      kicker.textContent = kickerText;
      title.replaceChildren();
      meta.replaceChildren();
      icons.replaceChildren();
      const iconUrls = details.icons || [];
      const titleLines = wrappedHubLines(details.title, layout.titleLineLength);
      const metaLines = wrappedHubLines(details.meta, layout.metaLineLength);
      const density =
        titleLines.length > 2 ? 'dense' : titleLines.length > 1 ? 'multiline' : 'single';
      const titleFontSize = layout.titleFontSizes[density];
      const titleLineHeight = layout.titleLineHeights[density];
      const items = [];
      if (kickerText !== '') {
        items.push({
          key: 'kicker',
          height: layout.kickerFontSize * 1.3,
        });
      }
      if (iconUrls.length > 0) {
        items.push({ key: 'icons', height: layout.iconSize });
      }
      items.push({
        key: 'title',
        height: titleFontSize * 1.3 + (titleLines.length - 1) * titleLineHeight,
      });
      if (metaLines.length > 0) {
        items.push({
          key: 'meta',
          height:
            layout.statusFontSize * 1.3 +
            (metaLines.length - 1) * layout.statusLineHeight,
        });
      }
      const availableTop = center - layout.radius + layout.padding;
      const availableBottom = center + layout.radius - layout.padding;
      const availableHeight = availableBottom - availableTop;
      const itemHeight = items.reduce((sum, item) => sum + item.height, 0);
      const gapCount = Math.max(0, items.length - 1);
      const naturalGap = gapCount > 0 ? (availableHeight - itemHeight) / gapCount : 0;
      const gap = Math.max(
        layout.minimumGap,
        Math.min(layout.maximumGap, naturalGap),
      );
      const stackHeight = itemHeight + gap * gapCount;
      let cursor = availableTop + Math.max(0, (availableHeight - stackHeight) / 2);
      const positions = new Map();
      items.forEach((item, index) => {
        positions.set(item.key, cursor);
        cursor += item.height + (index < items.length - 1 ? gap : 0);
      });
      if (kickerText !== '') {
        kicker.setAttribute('y', String(positions.get('kicker') + layout.kickerFontSize));
      }
      if (iconUrls.length > 0) {
        const iconSize = layout.iconSize;
        const iconStep = iconSize + layout.iconGap;
        const firstX = center - ((iconUrls.length - 1) * iconStep) / 2;
        iconUrls.forEach((iconUrl, index) => {
          addIcon(
            icons,
            iconUrl,
            firstX + index * iconStep,
            positions.get('icons') + iconSize / 2,
            iconSize,
            'product-spirit-aroma__wheel-center-icon',
          );
        });
      }
      const titleText = addTextLines(
        title,
        titleLines,
        center,
        positions.get('title') +
          titleFontSize +
          ((titleLines.length - 1) * titleLineHeight) / 2,
        'product-spirit-aroma__wheel-center-title-text',
        titleLineHeight,
      );
      if (density !== 'single') {
        titleText.classList.add(
          `product-spirit-aroma__wheel-center-title-text--${density}`,
        );
      }
      if (metaLines.length > 0) {
        addTextLines(
          meta,
          metaLines,
          center,
          positions.get('meta') +
            layout.statusFontSize +
            ((metaLines.length - 1) * layout.statusLineHeight) / 2,
          'product-spirit-aroma__wheel-center-status-text',
          layout.statusLineHeight,
        );
      }
    };
    const dominantSubfamilies = model.activeSubfamilies.filter(
      (subfamily) => subfamily.status === 'dominant',
    );
    const dominantAromas = dominantSubfamilies.flatMap((subfamily) =>
      subfamily.aromas.filter((aroma) => aroma.intensity === 'dominant'),
    );
    const secondaryAromas = model.contract.aromas.filter(
      (aroma) => aroma.intensity === 'secondary',
    );
    let defaults;
    if (dominantAromas.length === 0) {
      defaults = {
        title: model.contract.labels.profileCenter,
        meta: `${secondaryAromas.length} × ${model.contract.labels.secondary}`,
        kicker: '',
        icons: [],
      };
    } else if (dominantAromas.length <= 2) {
      defaults = {
        title: dominantAromas.map((aroma) => aroma.name).join(' & '),
        meta: `${secondaryAromas.length} × ${model.contract.labels.secondary}`,
        kicker: model.contract.labels.dominant,
        icons: dominantSubfamilies.map((subfamily) => subfamily.iconUrl),
      };
    } else {
      defaults = {
        title: `${dominantAromas.length} × ${model.contract.labels.dominant}`,
        meta: `${secondaryAromas.length} × ${model.contract.labels.secondary}`,
        kicker: model.contract.labels.profileCenter,
        icons: [],
      };
    }
    return { set, defaults };
  }

  function createFocusedHub(svg, model, family, compact) {
    const center = 380;
    const kickerY = compact ? center - 74 : center - 60;
    svg.append(
      createSvgElement('circle', {
        class: 'product-spirit-aroma__wheel-center',
        cx: center,
        cy: center,
        r: 148,
      }),
    );
    const live = createSvgElement('g', {
      class: 'product-spirit-aroma__wheel-center-live',
      'aria-live': 'polite',
      'aria-atomic': 'true',
    });
    const kicker = createSvgElement('text', {
      class: 'product-spirit-aroma__wheel-center-kicker',
      x: center,
      y: kickerY,
      'text-anchor': 'middle',
    });
    const title = createSvgElement('g', {
      class: 'product-spirit-aroma__wheel-center-title',
    });
    const meta = createSvgElement('g', {
      class: 'product-spirit-aroma__wheel-center-status',
    });
    live.append(kicker, title, meta);
    svg.append(live);
    const set = (details) => {
      kicker.textContent = cleanText(details.kicker).toUpperCase();
      title.replaceChildren();
      meta.replaceChildren();
      const titleLines = wrappedLines(details.title, compact ? 11 : 12);
      const metaLines = wrappedLines(details.meta, 18);
      const titleLineHeight =
        titleLines.length > 2 ? (compact ? 34 : 27) : compact ? 38 : 30;
      const metaLineHeight = compact ? 29 : 20;
      const baselineGap = compact ? 58 : 40;
      const titleSpan = (titleLines.length - 1) * titleLineHeight;
      const metaSpan = (metaLines.length - 1) * metaLineHeight;
      const totalSpan = titleSpan + baselineGap + metaSpan;
      const contentCenter = center + (compact ? 20 : 25);
      const firstTitleBaseline = contentCenter - totalSpan / 2;
      const titleY = firstTitleBaseline + titleSpan / 2;
      const firstMetaBaseline = firstTitleBaseline + titleSpan + baselineGap;
      const metaY = firstMetaBaseline + metaSpan / 2;
      const defaultKickerY = compact ? center - 74 : center - 60;
      const kickerGap = compact ? 56 : 40;
      kicker.setAttribute(
        'y',
        Math.min(defaultKickerY, firstTitleBaseline - kickerGap),
      );
      const titleText = addTextLines(
        title,
        titleLines,
        center,
        titleY,
        'product-spirit-aroma__wheel-center-title-text',
        titleLineHeight,
      );
      if (titleLines.length > 1) {
        titleText.classList.add(
          'product-spirit-aroma__wheel-center-title-text--multiline',
        );
      }
      if (titleLines.length > 2) {
        titleText.classList.add(
          'product-spirit-aroma__wheel-center-title-text--dense',
        );
      }
      addTextLines(
        meta,
        metaLines,
        center,
        metaY,
        'product-spirit-aroma__wheel-center-status-text',
        metaLineHeight,
      );
    };
    return {
      set,
      defaults: {
        title: family.name,
        meta: `${family.subfamilies.length} ${model.contract.labels.ofSubfamilies}`,
        kicker: interactionLabel(model.contract, family.status),
      },
    };
  }

  function bindWheelInteractions(svg, entries, hub, options = {}) {
    const reset = () => {
      if (options.onReset) {
        options.onReset();
        return;
      }
      clearSelection(svg);
      hub.set(hub.defaults);
    };
    const restore = () => {
      const active = entries.find(({ piece }) => piece.classList.contains('is-active'));
      hub.set(active ? active.details : hub.defaults);
    };
    entries.forEach(({ piece, details, activate }) => {
      piece.addEventListener('pointerenter', () => hub.set(details));
      piece.addEventListener('focus', () => hub.set(details));
      const select = (event) => {
        clearPeerSelections(svg);
        if (activate) {
          activate(event);
          return;
        }
        const wasActive = piece.classList.contains('is-active');
        clearSelection(svg);
        if (!wasActive) {
          piece.classList.add('is-active');
          piece.setAttribute('aria-pressed', 'true');
          svg.classList.add('is-interacting');
          hub.set(details);
        } else {
          hub.set(hub.defaults);
        }
      };
      piece.addEventListener('click', select);
      piece.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          select(event);
        }
      });
    });
    hub.set(hub.defaults);
    svg.addEventListener(RESET_SELECTION_EVENT, reset);
    svg.addEventListener('pointerleave', restore);
    svg.addEventListener('focusout', (event) => {
      if (!svg.contains(event.relatedTarget)) {
        restore();
      }
    });
    svg.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      if (options.onEscape) {
        options.onEscape();
      } else {
        reset();
      }
    });
  }

  function createOverviewWheel(model, options) {
    const compact = options.compact === true;
    const showFamilyLabels = options.showFamilyLabels === true;
    const prominentHub = options.prominentHub === true;
    const size = 900;
    const center = 450;
    const familyInner = 252;
    const familyOuter = 404;
    const svg = createSvgElement('svg', {
      class: `product-spirit-aroma__wheel-svg product-spirit-aroma__wheel-svg--quiet${compact ? ' product-spirit-aroma__wheel-svg--compact' : ''}${prominentHub ? ' product-spirit-aroma__wheel-svg--prominent' : ''}`,
      viewBox: `0 0 ${size} ${size}`,
      role: 'group',
      'aria-label': model.contract.labels.wheelInstructions,
      focusable: 'false',
    });
    svg.append(
      createSvgElement('circle', {
        class: 'product-spirit-aroma__wheel-background',
        cx: center,
        cy: center,
        r: familyOuter + 2,
      }),
    );
    const familyLayer = createSvgElement('g', {
      class: 'product-spirit-aroma__wheel-layer product-spirit-aroma__wheel-layer--families',
    });
    svg.append(familyLayer);
    const familyStep = 360 / model.families.length;
    const startOffset = -90 - familyStep / 2;
    const familyNodes = [];
    const entries = [];
    const geometryFor = (familyIndex) => {
      const startAngle = startOffset + familyIndex * familyStep;
      return { startAngle, endAngle: startAngle + familyStep };
    };
    const familyBox = (geometry, maximumFontSize) => ({
      center,
      startAngle: geometry.startAngle,
      endAngle: geometry.endAngle,
      innerRadius: familyInner,
      outerRadius: familyOuter,
      anchorRadius: (familyInner + familyOuter) / 2,
      iconSize: showFamilyLabels ? 30 : 0,
      maximumFontSize,
      minimumFontSize: compact ? 18 : showFamilyLabels ? 12 : 10,
      maximumLines: 3,
      padding: 11,
    });
    const fittedSizes = showFamilyLabels
      ? model.families.map((family, familyIndex) =>
          fitSectorBlock(
            family.name,
            '',
            familyBox(geometryFor(familyIndex), 20),
          ).fontSize,
        )
      : [];
    const sortedSizes = [...fittedSizes].sort((first, second) => second - first);
    const uniformFontSize = showFamilyLabels
      ? sortedSizes[Math.min(7, sortedSizes.length - 1)]
      : compact
        ? 34
        : 16;
    model.families.forEach((family, familyIndex) => {
      const geometry = geometryFor(familyIndex);
      const statusLabel = interactionLabel(model.contract, family.status);
      const familyPiece = wheelPiece({
        center,
        innerRadius: familyInner,
        outerRadius: familyOuter,
        startAngle: geometry.startAngle + 0.5,
        endAngle: geometry.endAngle - 0.5,
        hoverDistance: 7,
        key: family.key,
        type: 'family',
        status: family.status,
        color: family.color,
        foreground: family.foreground,
        iconFilter: family.iconFilter,
        ariaLabel: `${family.name}: ${statusLabel}`,
      });
      const middleAngle = (geometry.startAngle + geometry.endAngle) / 2;
      if (showFamilyLabels) {
        const fitted = fitSectorBlock(
          family.name,
          '',
          familyBox(geometry, uniformFontSize),
        );
        if (fitted.iconY !== null) {
          addIcon(
            familyPiece.visual,
            family.iconUrl,
            fitted.x,
            fitted.iconY,
            30,
            'product-spirit-aroma__wheel-icon',
          );
        }
        const label = addTextLines(
          familyPiece.visual,
          fitted.lines,
          fitted.x,
          fitted.titleY,
          'product-spirit-aroma__wheel-label product-spirit-aroma__wheel-label--family',
          fitted.lineHeight,
        );
        label.style.fontSize = `${fitted.fontSize}px`;
      } else {
        const point = polarPoint(
          center,
          (familyInner + familyOuter) / 2,
          middleAngle,
        );
        addIcon(
          familyPiece.visual,
          family.iconUrl,
          point.x,
          point.y,
          62,
          'product-spirit-aroma__wheel-icon',
        );
      }
      familyLayer.append(familyPiece.piece);
      familyNodes.push(familyPiece.piece);
      entries.push({
        piece: familyPiece.piece,
        details: {
          title: family.name,
          meta: `${family.subfamilies.length} ${model.contract.labels.ofSubfamilies}`,
          kicker: statusLabel,
        },
        activate: options.onFamily
          ? (event) =>
              options.onFamily(
                family,
                event.type === 'keydown' || event.detail === 0,
              )
          : null,
      });
    });
    const hub = createOverviewHub(svg, model, compact, prominentHub);
    bindWheelInteractions(svg, entries, hub);
    ringKeyboard(familyNodes);
    return svg;
  }

  function createFamilyPicker(model, onPick) {
    const picker = createHtmlElement('div', {
      class: 'product-spirit-aroma__picker',
    });
    picker.append(
      createHtmlElement('p', {
        class: 'product-spirit-aroma__picker-hint',
        text: model.contract.labels.tapHint,
      }),
    );
    const list = createHtmlElement('ul', {
      class: 'product-spirit-aroma__picker-list',
      role: 'list',
    });
    model.families.forEach((family) => {
      const item = createHtmlElement('li');
      const statusLabel = interactionLabel(model.contract, family.status);
      const button = createHtmlElement('button', {
        type: 'button',
        class: `product-spirit-aroma__picker-item product-spirit-aroma__picker-item--${family.status}`,
        style: `--spirit-aroma-family-color: ${family.color}`,
        'data-spirit-aroma-picker-key': family.key,
        'data-spirit-aroma-cue': `family:${family.key}`,
      });
      button.append(
        createHtmlElement('span', {
          class: 'product-spirit-aroma__picker-dot',
          'aria-hidden': 'true',
        }),
        createHtmlElement('span', {
          class: 'product-spirit-aroma__picker-name',
          text: family.name,
        }),
      );
      if (family.status !== 'inactive') {
        button.append(
          createHtmlElement('span', {
            class: 'product-spirit-aroma__picker-badge',
            text: statusLabel,
          }),
        );
      }
      button.addEventListener('click', (event) =>
        onPick(family, event.detail === 0),
      );
      item.append(button);
      list.append(item);
    });
    picker.append(list);
    return picker;
  }

  function renderWheelOverview(root, mount, model, options, focusFamilyKey) {
    mount.dataset.spiritAromaWheelMode = 'overview';
    const openFamily = (family, focusBack = false) =>
      renderFocusedFamily(root, mount, model, family, options, focusBack);
    const svg = createOverviewWheel(model, {
      compact: options.compact,
      showFamilyLabels: options.showFamilyLabels,
      prominentHub: options.prominentHub,
      onFamily: openFamily,
    });
    const children = [svg];
    if (options.showPicker) {
      children.push(createFamilyPicker(model, openFamily));
    }
    mount.replaceChildren(...children);
    bindCues(root);
    if (focusFamilyKey) {
      const focusTarget = options.showPicker
        ? mount.querySelector(`[data-spirit-aroma-picker-key="${focusFamilyKey}"]`)
        : mount.querySelector(
            `[data-spirit-aroma-type="family"][data-spirit-aroma-key="${focusFamilyKey}"]`,
          );
      if (focusTarget) {
        focusTarget.focus();
      }
    }
  }

  function renderFocusedFamily(root, mount, model, family, options, focusBack = false) {
    mount.dataset.spiritAromaWheelMode = 'family';
    const back = createHtmlElement('button', {
      class: 'product-spirit-aroma__back',
      type: 'button',
      text: `← ${model.contract.labels.backToOverview}`,
    });
    const returnToOverview = (restoreFocus = true) =>
      renderWheelOverview(
        root,
        mount,
        model,
        options,
        restoreFocus ? family.key : null,
      );
    back.addEventListener('click', (event) => returnToOverview(event.detail === 0));
    back.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      returnToOverview();
    });
    const size = 760;
    const center = 380;
    const subfamilyInner = 162;
    const subfamilyOuter = 293;
    const familyInner = 305;
    const familyOuter = 341;
    const svg = createSvgElement('svg', {
      class: `product-spirit-aroma__wheel-svg product-spirit-aroma__wheel-svg--compact product-spirit-aroma__wheel-svg--focused product-spirit-aroma__wheel-svg--focused-${options.compact ? 'mobile' : 'desktop'}`,
      viewBox: `0 0 ${size} ${size}`,
      role: 'group',
      'aria-label': `${family.name} – ${family.subfamilies.length} ${model.contract.labels.ofSubfamilies}. ${model.contract.labels.wheelInstructions}`,
      focusable: 'false',
    });
    const familyNodes = [];
    const subfamilyNodes = [];
    const entries = [];
    const initialSubfamily = family.subfamilies.find(
      (subfamily) => subfamily.status === family.status && subfamily.status !== 'inactive',
    );
    let initialSubfamilyEntry;
    const familyStep = 360 / model.families.length;
    model.families.forEach((candidate, index) => {
      const startAngle = -90 - familyStep / 2 + index * familyStep;
      const endAngle = startAngle + familyStep;
      const isOpen = candidate.key === family.key;
      const status = isOpen ? candidate.status : 'orientation';
      const piece = wheelPiece({
        center,
        innerRadius: familyInner,
        outerRadius: familyOuter,
        startAngle: startAngle + 0.8,
        endAngle: endAngle - 0.8,
        hoverDistance: 4,
        key: candidate.key,
        type: 'family',
        status,
        color: candidate.color,
        foreground: candidate.foreground,
        iconFilter: candidate.iconFilter,
        ariaLabel: `${candidate.name}: ${interactionLabel(model.contract, candidate.status)}`,
      });
      piece.piece.classList.add(
        isOpen
          ? 'product-spirit-aroma__wheel-piece--orientation-open'
          : 'product-spirit-aroma__wheel-piece--orientation-muted',
      );
      if (isOpen) {
        piece.piece.setAttribute('aria-pressed', 'true');
      }
      if (!isOpen && candidate.status !== 'inactive') {
        piece.piece.classList.add(
          'product-spirit-aroma__wheel-piece--orientation-colored',
        );
      }
      svg.append(piece.piece);
      familyNodes.push(piece.piece);
      entries.push({
        piece: piece.piece,
        details: {
          title: candidate.name,
          meta: `${candidate.subfamilies.length} ${model.contract.labels.ofSubfamilies}`,
          kicker: interactionLabel(model.contract, candidate.status),
        },
        activate: (event) =>
          isOpen
            ? returnToOverview(event.type === 'keydown' || event.detail === 0)
            : renderFocusedFamily(
                root,
                mount,
                model,
                candidate,
                options,
                event.type === 'keydown' || event.detail === 0,
              ),
      });
    });
    const count = family.subfamilies.length;
    const maximumSubfamilyFontSize =
      count > 5
        ? options.compact
          ? 24
          : 16
        : options.compact
          ? 27
          : 18;
    const minimumSubfamilyFontSize = options.compact ? 16 : 12;
    const step = 360 / count;
    family.subfamilies.forEach((subfamily, index) => {
      const startAngle = -90 - step / 2 + index * step;
      const endAngle = startAngle + step;
      const statusLabel = interactionLabel(model.contract, subfamily.status);
      const piece = wheelPiece({
        center,
        innerRadius: subfamilyInner,
        outerRadius: subfamilyOuter,
        startAngle: startAngle + 0.7,
        endAngle: endAngle - 0.7,
        hoverDistance: 6,
        key: subfamily.key,
        type: 'subfamily',
        status: subfamily.status,
        color: family.color,
        foreground: family.foreground,
        iconFilter: family.iconFilter,
        ariaLabel: `${subfamily.name}: ${statusLabel}`,
      });
      const aromaText = subfamily.aromas.map((aroma) => aroma.name).join(', ');
      const fitted = fitSectorBlock(subfamily.name, aromaText, {
        center,
        startAngle,
        endAngle,
        innerRadius: subfamilyInner,
        outerRadius: subfamilyOuter,
        anchorRadius: (subfamilyInner + subfamilyOuter) / 2,
        iconSize: 0,
        maximumFontSize: maximumSubfamilyFontSize,
        minimumFontSize: minimumSubfamilyFontSize,
        maximumLines: 3,
        padding: 11,
      });
      const title = addTextLines(
        piece.visual,
        fitted.lines,
        fitted.x,
        fitted.titleY,
        'product-spirit-aroma__wheel-label product-spirit-aroma__wheel-label--focused',
        fitted.lineHeight,
      );
      title.style.fontSize = `${fitted.fontSize}px`;
      if (fitted.detail) {
        const detail = addTextLines(
          piece.visual,
          [fitted.detail],
          fitted.x,
          fitted.detailY,
          'product-spirit-aroma__wheel-label product-spirit-aroma__wheel-label--detail',
          fitted.detailSize,
        );
        detail.style.fontSize = `${fitted.detailSize.toFixed(2)}px`;
      }
      svg.append(piece.piece);
      const detailText =
        subfamily.aromas.length > 0
          ? subfamily.aromas.map((aroma) => aroma.name).join(' · ')
          : subfamily.hints.length > 0
            ? subfamily.hints.map((aroma) => aroma.name).join(' · ')
            : model.contract.labels.noneActive;
      subfamilyNodes.push(piece.piece);
      const entry = {
        piece: piece.piece,
        details: {
          title: subfamily.name,
          meta: detailText,
          kicker: statusLabel,
        },
      };
      entries.push(entry);
      if (subfamily === initialSubfamily) {
        initialSubfamilyEntry = entry;
      }
    });
    const hub = createFocusedHub(svg, model, family, options.compact);
    bindWheelInteractions(svg, entries, hub, {
      onEscape: () => returnToOverview(),
      onReset: () => returnToOverview(false),
    });
    ringKeyboard(familyNodes);
    ringKeyboard(subfamilyNodes, initialSubfamily ? family.subfamilies.indexOf(initialSubfamily) : 0);
    if (initialSubfamilyEntry) {
      initialSubfamilyEntry.piece.classList.add('is-active');
      initialSubfamilyEntry.piece.setAttribute('aria-pressed', 'true');
      svg.classList.add('is-interacting');
      hub.set(initialSubfamilyEntry.details);
    }
    mount.replaceChildren(back, svg);
    bindCues(root);
    if (focusBack) {
      back.focus();
    }
  }

  function renderWheels(root, contract) {
    const desktopMount = root.querySelector('[data-spirit-aroma-wheel-desktop]');
    const mobileMount = root.querySelector('[data-spirit-aroma-wheel-mobile]');
    assertContract(desktopMount && mobileMount, 'wheel_mount_missing');
    const model = wheelModel(contract);
    renderWheelOverview(root, desktopMount, model, {
      compact: false,
      prominentHub: true,
      showFamilyLabels: true,
      showPicker: false,
    });
    renderWheelOverview(root, mobileMount, model, {
      compact: true,
      prominentHub: false,
      showFamilyLabels: false,
      showPicker: true,
    });
    bindCues(root);
    bindClickAway(root);
  }

  function initializeRoot(root) {
    const dataNode = root.querySelector('[data-spirit-aroma-profile-contract]');
    if (!dataNode) {
      root.hidden = true;
      root.dataset.spiritAromaProfileState = 'invalid';
      root.dataset.spiritAromaProfileReason = 'contract_missing';
      return;
    }
    try {
      const contract = validateContract(JSON.parse(dataNode.textContent || ''));
      if (root.dataset.spiritAromaProfileInitialized === CONTRACT_VERSION) {
        return;
      }
      if (contract.renderMode === 'ready_wheel') {
        renderWheels(root, contract);
      }
      root.hidden = false;
      root.dataset.spiritAromaProfileInitialized = CONTRACT_VERSION;
      root.dataset.spiritAromaProfileRenderMode = contract.renderMode;
      root.dataset.spiritAromaProfileState = 'enhanced';
      delete root.dataset.spiritAromaProfileReason;
    } catch (error) {
      root.hidden = true;
      root.dataset.spiritAromaProfileState = 'invalid';
      root.dataset.spiritAromaProfileReason =
        error && typeof error.code === 'string' ? error.code : 'contract_parse_failed';
    }
  }

  function initializeWithin(scope) {
    const roots = [];
    if (scope && scope.matches && scope.matches('[data-spirit-aroma-profile]')) {
      roots.push(scope);
    }
    if (scope && scope.querySelectorAll) {
      roots.push(...scope.querySelectorAll('[data-spirit-aroma-profile]'));
    }
    roots.forEach(initializeRoot);
  }

  function initialize() {
    initializeWithin(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeWithin(event.target);
  });
})();
